import React, { useState, useCallback } from 'react';
import Papa from 'papaparse';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { usePortfolioHoldings } from '@/hooks/usePortfolioHoldings';
import { toast } from '@/hooks/use-toast';
import { Upload, FileText, AlertCircle, CheckCircle } from 'lucide-react';

interface CSVImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ParsedRow {
  symbol: string;
  quantity: number;
  average_price: number;
  asset_type: string;
  valid: boolean;
  error?: string;
}

export const CSVImportModal: React.FC<CSVImportModalProps> = ({ open, onOpenChange }) => {
  const { upsertBatch } = usePortfolioHoldings();
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [fileName, setFileName] = useState('');

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h: string) => h.trim().toLowerCase().replace(/\s+/g, '_'),
      complete: (results) => {
        const rows: ParsedRow[] = results.data.map((row: any) => {
          const symbol = (row.symbol || '').toString().trim().toUpperCase();
          const quantity = parseFloat(row.quantity);
          const avgPrice = parseFloat(row.average_price || row.avg_price || row.price || '0');
          const assetType = (row.asset_type || row.type || 'stock').toString().trim().toLowerCase();

          const validTypes = ['stock', 'crypto', 'forex', 'commodity', 'etf', 'bond', 'mutual_fund'];
          let error: string | undefined;
          if (!symbol) error = 'Missing symbol';
          else if (isNaN(quantity) || quantity <= 0) error = 'Invalid quantity';
          else if (isNaN(avgPrice) || avgPrice <= 0) error = 'Invalid price';
          else if (!validTypes.includes(assetType)) error = `Invalid type: ${assetType}`;

          return { symbol, quantity, average_price: avgPrice, asset_type: assetType, valid: !error, error };
        });
        setParsedRows(rows);
      },
    });
  }, []);

  const validRows = parsedRows.filter(r => r.valid);
  const invalidRows = parsedRows.filter(r => !r.valid);

  const handleImport = async () => {
    if (validRows.length === 0) return;
    setImporting(true);
    try {
      const count = await upsertBatch(validRows.map(r => ({
        symbol: r.symbol,
        asset_type: r.asset_type,
        quantity: r.quantity,
        average_price: r.average_price,
        source: 'csv_import',
      })));
      toast({ title: 'Import complete', description: `Imported ${count} holdings successfully.` });
      setParsedRows([]);
      setFileName('');
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: 'Import failed', description: err.message, variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = (v: boolean) => {
    if (!v) { setParsedRows([]); setFileName(''); }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Upload className="h-5 w-5 text-primary" />
            Import CSV
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {parsedRows.length === 0 ? (
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-3">
                Upload a CSV with columns: <span className="font-mono text-foreground">Symbol, Quantity, Average Price, Asset Type</span>
              </p>
              <label className="cursor-pointer">
                <Button variant="outline" asChild><span>Choose File</span></Button>
                <input type="file" accept=".csv" onChange={handleFileChange} className="hidden" />
              </label>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{fileName}</span> — {validRows.length} valid, {invalidRows.length} invalid
                </p>
                <label className="cursor-pointer">
                  <Button variant="ghost" size="sm" asChild><span>Change file</span></Button>
                  <input type="file" accept=".csv" onChange={handleFileChange} className="hidden" />
                </label>
              </div>

              <div className="border border-border rounded-md overflow-auto max-h-60">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Symbol</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Avg Price</TableHead>
                      <TableHead>Type</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedRows.map((row, i) => (
                      <TableRow key={i} className={row.valid ? '' : 'bg-destructive/5'}>
                        <TableCell>
                          {row.valid ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-destructive" />
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{row.symbol || '—'}</TableCell>
                        <TableCell>{row.valid ? row.quantity : '—'}</TableCell>
                        <TableCell>{row.valid ? row.average_price.toFixed(2) : '—'}</TableCell>
                        <TableCell>
                          {row.valid ? (
                            <Badge variant="secondary" className="text-xs">{row.asset_type}</Badge>
                          ) : (
                            <span className="text-xs text-destructive">{row.error}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
          <Button onClick={handleImport} disabled={importing || validRows.length === 0}>
            {importing ? 'Importing...' : `Import ${validRows.length} Holdings`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
